/**
 * Hook to fetch custom field definitions for contacts from CRM ingestion metadata
 *
 * This is the proper way to get custom field definitions - query the field metadata
 * from the backend rather than iterating through contact records.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  useGetCrmIngestionFieldsLazyQuery,
  CrmIngestionPlatform,
  CrmIngestionObjectType,
} from '@/lib/graphql/types-and-hooks';
import { useWorkspaceId } from '@/lib/hooks/use-workspace-id';
import { usePreviewCustomFieldsFor, mergeCustomFieldLists } from '@/lib/hooks/use-preview-custom-fields';
import {
  customFieldsFromCrmIngestion,
  standardFieldsFromCrmIngestion,
  type CustomFieldDefinition,
} from '@/lib/utils/crm-custom-fields';

export type { CustomFieldDefinition };

export const useContactCustomFields = () => {
  const workspaceId = useWorkspaceId();
  const [customFields, setCustomFields] = useState<CustomFieldDefinition[]>([]);
  const previewFields = usePreviewCustomFieldsFor('Contact');
  // True once the definitions query has settled (data or error), i.e. `customFields`
  // reflects the backend rather than the initial []. Set in the same effect that
  // sets customFields so consumers never observe hydrated=true with stale fields.
  // Lets consumers (grid summaries) wait for the final column set instead of
  // firing once for standard columns and again when custom columns arrive.
  const [hydrated, setHydrated] = useState(false);

  const [getCrmIngestionFields, { data, loading, error }] =
    useGetCrmIngestionFieldsLazyQuery({
      // cache-first: field definitions rarely change, and every flow that
      // changes them refreshes this operation explicitly (refetchQueries by
      // operation name in use-custom-field-mutations, cache evict in
      // use-integration-setup). cache-and-network made every mounting consumer
      // (page grid + always-mounted detail sheet) re-hit the network with
      // identical variables, duplicating an expensive query per page load.
      fetchPolicy: 'cache-first',
    });

  useEffect(() => {
    if (workspaceId) {
      getCrmIngestionFields({
        variables: {
          workspaceId: parseInt(workspaceId),
          platform: CrmIngestionPlatform.Coffee,
          first: 100, // Get first 100 custom fields (should be enough)
        },
      });
    }
  }, [workspaceId, getCrmIngestionFields]);

  useEffect(() => {
    if (data?.crmIngestionFields?.edges) {
      // Shared pure mapping (also used by the idle prefetch).
      setCustomFields(customFieldsFromCrmIngestion(data, CrmIngestionObjectType.Contact));
    }
    // Error also hydrates: consumers should proceed with standard columns rather
    // than wait forever for definitions that will never arrive.
    if (data || error) setHydrated(true);
  }, [data, error]);

  // Memoize the merge so consumers get a stable array reference across renders.
  // Without this, a fresh array every render defeats downstream useMemo/useEffect
  // that depend on `customFields` by identity (these hooks feed the grids and all
  // three detail sheets).
  const mergedCustomFields = useMemo(
    () => mergeCustomFieldLists(customFields, previewFields as CustomFieldDefinition[]),
    [customFields, previewFields],
  );

  // Standard (non-custom) field definitions for this object. Formula fields
  // reference them by `crmName`, and the backend validates references against
  // the very same rows, so this list is the safe vocabulary to offer.
  const standardFields = useMemo(
    () => standardFieldsFromCrmIngestion(data, CrmIngestionObjectType.Contact),
    [data],
  );

  return {
    customFields: mergedCustomFields,
    standardFields,
    loading,
    error,
    hydrated,
  };
};
